import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, History, Loader2, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DayField } from "@/components/DayField";
import { ExportButton } from "@/components/ExportButton";
import { api, apiError, fmtWhen } from "@/lib/api";
import { useFreshest } from "@/lib/freshest";
import { SortBar } from "@/components/SortHeader";
import { sortRows, useSort } from "@/lib/sorting";
import { useAuth } from "@/context/AuthContext";

const PAGE = 25;

const ENTITY_LABELS = {
  project: "Use case",
  register: "Unit register",
  training: "Training data",
  user: "Officer",
  report: "Report",
  settings: "Settings",
};

export default function Activity() {
  const { scopedPlant, canReadAll, superAdmin, shows } = useAuth();
  const [data, setData] = useState(null);
  const [action, setAction] = useState("all");
  const [entity, setEntity] = useState("all");
  const [actor, setActor] = useState("all");
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [page, setPage] = useState(0);
  const [removing, setRemoving] = useState(null);

  const filters = {
    action: action === "all" ? "" : action,
    entity_type: entity === "all" ? "" : entity,
    actor: actor === "all" ? "" : actor,
    q,
    start,
    end,
  };

  const { start: freshStart, isFresh } = useFreshest();
  const { sort, on: sortBy } = useSort("activity");

  const load = useCallback(() => {
    // Only the newest request may put rows on the screen.
    const ticket = freshStart();
    api
      .get("/activity", {
        params: {
          action: action === "all" ? "" : action,
          entity_type: entity === "all" ? "" : entity,
          actor: actor === "all" ? "" : actor,
          q,
          start,
          end,
          skip: page * PAGE,
          limit: PAGE,
        },
      })
      .then((r) => isFresh(ticket) && setData(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, entity, actor, q, start, end, page]);

  const remove = async () => {
    const entry = removing;
    setRemoving(null);
    try {
      await api.delete(`/activity/${entry.id}`);
      toast.success("That entry is off the trail.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [action, entity, actor, q, start, end]);

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const from = data.total ? page * PAGE + 1 : 0;
  const to = Math.min((page + 1) * PAGE, data.total);

  return (
    <div className="p-5 sm:p-8" data-testid="activity-page">
      <div className="label-caps">Activity trail</div>
      <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
        Everything that changed
      </h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        {scopedPlant && !canReadAll
          ? `Every change made to ${scopedPlant} data — who did it, when, and what moved from what to what.`
          : "Every change made in the portal — status updates, register imports, readings, reports mailed, access changes."}
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="activity-search"
            placeholder="Search a use case, officer or remark…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-11 rounded-none bg-card pl-9"
          />
        </div>
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger className="h-11 w-[160px] rounded-none bg-card" data-testid="activity-entity">
            <SelectValue placeholder="What" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            <SelectItem value="all">Everything</SelectItem>
            {data.entity_types.map((t) => (
              <SelectItem key={t} value={t}>
                {ENTITY_LABELS[t] || t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="h-11 w-[190px] rounded-none bg-card" data-testid="activity-action">
            <SelectValue placeholder="Type of change" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            <SelectItem value="all">Any change</SelectItem>
            {data.actions.map((a) => (
              <SelectItem key={a.value} value={a.value}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={actor} onValueChange={setActor}>
          <SelectTrigger className="h-11 w-[210px] rounded-none bg-card" data-testid="activity-actor">
            <SelectValue placeholder="Who" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            <SelectItem value="all">Anyone</SelectItem>
            {data.actors.map((a) => (
              <SelectItem key={a.email} value={a.email}>
                {a.name} ({a.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DayField
          className="w-[170px]"
          testId="activity-start"
          format="iso"
          tall
          placeholder="From · dd.mm.yyyy"
          value={start}
          onChange={setStart}
        />
        <DayField
          className="w-[170px]"
          testId="activity-end"
          format="iso"
          tall
          placeholder="To · dd.mm.yyyy"
          value={end}
          onChange={setEnd}
        />
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="font-data text-[11px] uppercase tracking-wider text-muted-foreground">
          Showing {from}–{to} of {data.total} change(s)
        </p>
        <div className="flex gap-2">
        {shows("activity.export") && (
          <ExportButton
            path="/activity/export"
            params={filters}
            testId="export-activity"
            label="Download this view"
            className="h-9"
          />
        )}
          <Button
            variant="outline"
            className="h-9 rounded-none"
            disabled={page === 0}
            data-testid="activity-prev"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-9 rounded-none"
            disabled={to >= data.total}
            data-testid="activity-next"
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mt-4 border border-border bg-card px-4 py-3">
        <SortBar
          testId="activity-sort"
          sort={sort}
          onSort={sortBy}
          note="within the page on screen"
          columns={[
            ["at", "When"],
            ["actor_name", "Who"],
            ["action", "Type of change"],
            ["plant", "Unit"],
            ["entity_type", "What"],
          ]}
        />
      </div>

      {data.items.length === 0 ? (
        <div className="mt-6 border border-border bg-card p-8">
          <History className="h-6 w-6 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            Nothing matches these filters. Try widening the dates or clearing the search.
          </p>
        </div>
      ) : (
        <ol className="mt-4 divide-y divide-border border border-border bg-card" data-testid="activity-list">
          {sortRows(data.items, sort.key, sort.dir).map((a, i) => (
            <li key={`${a.at}-${i}`} className="p-4" data-testid="activity-row">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-[260px] flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className="border border-border px-2 py-0.5 font-data text-[10px] uppercase tracking-wider text-muted-foreground"
                      data-testid="activity-action-tag"
                    >
                      {data.action_labels?.[a.action] || a.action}
                    </span>
                    {a.plant && (
                      <span className="border border-primary px-2 py-0.5 font-data text-[10px] uppercase tracking-wider text-primary">
                        {a.plant}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {ENTITY_LABELS[a.entity_type] || a.entity_type}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm font-medium leading-snug">{a.summary}</p>
                  {a.entity_label && (
                    <p className="font-data text-[11px] text-muted-foreground">{a.entity_label}</p>
                  )}
                  {a.changes?.length > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {a.changes.map((c, j) => (
                        <li key={`${c.field}-${j}`} className="text-[11px] text-muted-foreground">
                          {c.label}: <s>{String(c.from ?? "—")}</s> →{" "}
                          <b className="text-foreground">{String(c.to ?? "—")}</b>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="text-right">
                  <div className="font-data text-[11px]">{fmtWhen(a.at)}</div>
                  <div className="text-[11px] text-muted-foreground">{a.actor_name}</div>
                  <div className="font-data text-[10px] text-muted-foreground">{a.actor_email}</div>
                  {superAdmin && (
                    <button
                      type="button"
                      data-testid={`activity-delete-${a.id}`}
                      onClick={() => setRemoving(a)}
                      className="mt-2 inline-flex items-center gap-1.5 border border-[hsl(var(--at-risk))] px-2 py-1 font-display text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--at-risk))] transition-colors duration-200 hover:bg-[hsl(var(--at-risk))] hover:text-white"
                    >
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(v) => !v && setRemoving(null)}
        title="Take this entry off the trail?"
        description={
          removing
            ? `“${removing.summary}” — recorded ${fmtWhen(removing.at)}. It will be gone for good, and nothing will be written about its removal.`
            : ""
        }
        confirmLabel="Yes, remove it"
        cancelLabel="No, keep it"
        tone="danger"
        onConfirm={remove}
        testId="confirm-activity-delete"
      />
    </div>
  );
}
